import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ClassCardHeader,
  Drawer,
  IconButton,
  Input,
  NavItem,
  Skeleton,
  snack,
  Typography,
} from '@innovamat/glimmer-components';
import { type Student, useStudentsQuery } from '@innovamat/glow-api-client';

import { getAvatarImage } from '../../../../utils';
import { useUser } from '../../../user-management';
import { useClassroom } from '../../hooks/use-classroom';
import { useClassroomCapabilities } from '../../hooks/use-classroom-capabilities';
import { useClassrooms } from '../../hooks/use-classrooms';
import { useMoveStudent } from '../../hooks/use-move-student';
import { useOrganization } from '../../hooks/use-organization';
import { usePermissionCourses } from '../../hooks/use-permission-courses';
import {
  type Classroom,
  type ClassroomType,
  CLASSROOM_TYPE,
  isJourneyClassroom,
} from '../../modules/classroom';
import { SearchOrganizationInput } from '../search-organization-input';
import { MoveRTIStudentConfirmationModal } from './move-rti-student-confirmation-modal';

const Wrapper = styled.div`
  height: 100%;
  padding: 24px 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const DrawerHeader = styled.div`
  margin-bottom: 24px;
`;

const Subtitle = styled(Typography.Body2)`
  margin-top: 16px;
  margin-bottom: 8px;
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 0;
  > div {
    display: flex;
    flex-direction: column;
  }
  border-bottom: 1px solid
    ${({ theme }) => theme.tokens.color.alias.cm.border['border-subtle'].value};
`;

const ClassroomsContent = styled.div`
  margin-top: 8px;
  overflow-y: auto;
  flex: 1;
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

type Props = {
  showDrawer: boolean;
  student: Student | undefined;
  onClose: () => void;
  onSuccess?: (student: Student, oldStudent: Student) => void;
  classroomType?: ClassroomType;
};

function MoveStudentToClassroomDrawer({
  showDrawer,
  student,
  onClose,
  onSuccess,
  classroomType = CLASSROOM_TYPE.CLASSROOM,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { isRosteringEnabled } = useOrganization();
  const { user, isRole } = useUser();

  const [currentOrganizationId, setCurrentOrganizationId] = useState<string>(
    user?.organizationId!
  );

  const [openMoveRTIConfirmation, setOpenMoveRTIConfirmation] = useState(false);

  const {
    classrooms: allClassrooms,
    classroomsByStage,
    filterClassroomByDigitalPractice,
    isLoading: isLoadingClassrooms,
  } = useClassrooms({
    mine: false,
    organizationId: currentOrganizationId,
    type: classroomType,
  });

  const getClassroomId = () => {
    return student?.enrollments?.find((e) => e?.classroomType === classroomType)
      ?.classroomId;
  };

  const classroomId = getClassroomId();

  const { classroom } = useClassroom({ classroomId });
  const isJourney = isJourneyClassroom(classroom);
  const {
    reports: { canAccessIntervention },
  } = useClassroomCapabilities({ classroom: undefined });

  const { allPermissions } = usePermissionCourses({});

  const [classroomIdToMove, setClassroomIdToMove] = useState<
    string | undefined
  >(undefined);
  const [search, setSearch] = useState<string>('');

  const { onMoveStudent, isLoading } = useMoveStudent({
    currentStudent: student,
    updateEnrollment: classroomType === CLASSROOM_TYPE.JOURNEYS,
    onError: (errors) => {
      errors.response.errors.forEach((error) => {
        if (error.extensions?.response?.['status'] === 409) {
          if (
            JSON.parse(error.extensions?.response?.body).type ===
            'student.cannot_change_organization_to_another_data_center'
          ) {
            snack.error(t('errors.change-student-classroom.data-center'));
          } else {
            snack.error(t('students.alert.maxStudentsLength'));
          }
        } else {
          snack.error(t('errors.generic.title'));
        }
      });
    },
    onSuccess: (newStudent, oldStudent) => {
      snack.success(t('common.toast.success'));
      onClose();
      onSuccess?.(newStudent, oldStudent);
    },
  });

  useEffect(() => {
    setClassroomIdToMove(undefined);
    setSearch('');
  }, [showDrawer, currentOrganizationId]);

  const isCurrentClassroomJourneys = isJourneyClassroom(classroom);

  const studentRtiStatusUnknown =
    student?.rti?.enabled === null || student?.rti?.enabled === undefined;

  const { data: classroomStudents, isFetching: isFetchingStudents } =
    useStudentsQuery(
      {
        classroomId: classroomId!,
        organizationId: user?.organizationId!,
      },
      {
        enabled:
          studentRtiStatusUnknown && canAccessIntervention && !!classroomId,
        staleTime: Infinity,
      }
    );

  const classrooms = isJourney
    ? allClassrooms
    : classroomsByStage(student?.courseOrder!);

  const filterClassroomByType = (classroom: Classroom) => {
    return classroom.type === classroomType;
  };

  const filterClassroomBySearch = (classroom: Classroom) => {
    return classroom.name?.toLowerCase().includes(search.toLowerCase());
  };

  const filterClassroomByCurrentClassroom = (classroom: Classroom) => {
    return classroom.id !== classroomId;
  };

  const handleFilterClassroomByDigitalPractice = (classroom: Classroom) => {
    if (isJourney) {
      return true;
    }

    if (currentOrganizationId === user?.organizationId) {
      return filterClassroomByDigitalPractice(classroom);
    }

    return true;
  };

  const filteredClassrooms = useMemo(() => {
    return classrooms
      .filter(filterClassroomBySearch)
      .filter(filterClassroomByCurrentClassroom)
      .filter(filterClassroomByType)
      .filter(handleFilterClassroomByDigitalPractice);
  }, [
    classrooms,
    search,
    classroomId,
    allPermissions,
    isCurrentClassroomJourneys,
  ]);

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleMove = async () => {
    if (student?.rti?.enabled) {
      setOpenMoveRTIConfirmation(true);
      return;
    }

    if (studentRtiStatusUnknown && canAccessIntervention) {
      // TODO if the data of RTI in the students already arrives in the school and classroom responses, this code would be unnecessary
      const students = classroomStudents?.students ?? [];
      const studentIsRti = students.find((s) => s?.id === student?.id)?.rti
        ?.enabled;

      if (studentIsRti) {
        setOpenMoveRTIConfirmation(true);
      } else {
        executeMove();
      }
    } else {
      executeMove();
    }
  };

  const handleConfirmMove = () => {
    setOpenMoveRTIConfirmation(false);
    executeMove();
  };

  const executeMove = () => {
    const classroom = filteredClassrooms.find(
      (c) => c.id === classroomIdToMove
    );
    if (!classroom) return;
    onMoveStudent(
      classroom,
      currentOrganizationId !== user?.organizationId
        ? currentOrganizationId
        : undefined
    );
  };

  if (!classroom) return <></>;

  return (
    <>
      <MoveRTIStudentConfirmationModal
        isOpen={openMoveRTIConfirmation}
        onClose={() => setOpenMoveRTIConfirmation(false)}
        studentName={`${student?.firstName} ${student?.lastName}`}
        onConfirm={handleConfirmMove}
      />
      <Drawer open={showDrawer} customStyles={{ width: '470px' }} roundedBorder>
        <Wrapper>
          <DrawerHeader>
            <div>
              <IconButton icon="CloseIcon" onClick={onClose} />
            </div>
            <Header>
              <div>
                <Typography.H4>
                  {student?.firstName} {student?.lastName}
                </Typography.H4>
                <Subtitle>
                  {isCurrentClassroomJourneys
                    ? t('students.drawer.current-group')
                    : t('students.drawer.current-classroom')}
                </Subtitle>
                <ClassCardHeader
                  title={classroom.name ?? ''}
                  avatar={getAvatarImage({
                    avatar: classroom.avatar,
                    icon: classroom.icon,
                    courseOrder: classroom.courseOrder!,
                  })}
                  subtitle={classroom.courseName ?? ''}
                />
              </div>
            </Header>
          </DrawerHeader>

          {isRole.Advisor &&
            !isRosteringEnabled &&
            !isCurrentClassroomJourneys && (
              <>
                <SearchOrganizationInput
                  defaultValue={user?.organizationName}
                  onSelect={(organization) => {
                    setCurrentOrganizationId(organization.id!);
                  }}
                />
                <br />
              </>
            )}

          <Input
            labelText={t('common.move-to')}
            value={search}
            onChange={handleChangeSearch}
            leftIcon="SearchIcon"
          />

          <ClassroomsContent>
            {isFetchingStudents || isLoadingClassrooms
              ? Array.from({ length: 5 }).map((_, index) => (
                  <Skeleton key={index} height="50px" width="100%" radius="4" />
                ))
              : filteredClassrooms.map((classroom) => (
                  <NavItem
                    key={classroom.id}
                    name={classroom.name ?? ''}
                    avatar={getAvatarImage({
                      avatar: classroom.avatar,
                      icon: classroom.icon,
                      courseOrder: classroom.courseOrder!,
                    })}
                    selected={classroomIdToMove === classroom.id}
                    description={classroom.courseName ?? ''}
                    onClick={() => setClassroomIdToMove(classroom.id)}
                  />
                ))}
          </ClassroomsContent>

          <Footer>
            <Button
              fill
              variant="tertiary"
              onClick={() => setClassroomIdToMove(undefined)}
              disabled={
                !classroomIdToMove || isFetchingStudents || isLoadingClassrooms
              }
            >
              {t('common.cancel')}
            </Button>
            <Button
              fill
              disabled={
                !classroomIdToMove || isFetchingStudents || isLoadingClassrooms
              }
              onClick={handleMove}
              loading={isLoading}
            >
              {t('common.move')}
            </Button>
          </Footer>
        </Wrapper>
      </Drawer>
    </>
  );
}

export { MoveStudentToClassroomDrawer };
